import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetUsersForOrganizationV1OrganizationsOrganizationIdUsersGetData,
  type GetUsersForOrganizationV1OrganizationsOrganizationIdUsersGetError,
  type GetUsersForOrganizationV1OrganizationsOrganizationIdUsersGetResponse,
  getUsersForOrganizationV1OrganizationsOrganizationIdUsersGet,
} from '../../generated';
import { extractTotalCount } from '../../utils';

export type GetUsersForOrganizationQueryVariables =
  GetUsersForOrganizationV1OrganizationsOrganizationIdUsersGetData['path'] &
    GetUsersForOrganizationV1OrganizationsOrganizationIdUsersGetData['query'];

export type GetUsersForOrganizationQuery = {
  data: GetUsersForOrganizationV1OrganizationsOrganizationIdUsersGetResponse;
  total_count: number;
};

export const useGetUsersForOrganizationQuery = <
  TData = GetUsersForOrganizationQuery,
  TError = GetUsersForOrganizationV1OrganizationsOrganizationIdUsersGetError,
>(
  {
    organization_id,
    search_email,
    offset,
    limit,
    sort_by,
    sort_order,
    exclude_vizit,
  }: GetUsersForOrganizationQueryVariables,
  options?: Omit<UseQueryOptions<GetUsersForOrganizationQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetUsersForOrganizationQuery, TError, TData>({
    queryKey: [
      'GetUsersForOrganization',
      { organization_id, search_email, offset, limit, sort_by, sort_order, exclude_vizit },
    ],
    queryFn: async () => {
      const { data, response } = await getUsersForOrganizationV1OrganizationsOrganizationIdUsersGet({
        path: { organization_id },
        query: {
          search_email,
          offset: offset ? Number(offset) : undefined,
          limit: limit ? Number(limit) : undefined,
          sort_by,
          sort_order,
          exclude_vizit,
        },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useGetUsersForOrganizationQuery.getKey = (variables: GetUsersForOrganizationQueryVariables) => [
  'GetUsersForOrganization',
  variables,
];

useGetUsersForOrganizationQuery.fetcher = (
  {
    organization_id,
    search_email,
    offset,
    limit,
    sort_by,
    sort_order,
    exclude_vizit,
  }: GetUsersForOrganizationQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await getUsersForOrganizationV1OrganizationsOrganizationIdUsersGet({
      path: { organization_id },
      query: {
        search_email,
        offset: offset ? Number(offset) : undefined,
        limit: limit ? Number(limit) : undefined,
        sort_by,
        sort_order,
        exclude_vizit,
      },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};
